import { create } from 'zustand';
import { makeGet, makePatch, makePost } from '../../utils/call-api-endpoint';
import type { PaginatedList } from '../common/padinated-list';
import {
  toQueryString,
  type PaginationQueryParamsWithStatus
} from '../common/pagination-query-params';
import { useUIStore } from '../ui/ui.store';
import type { Corral, CorralParaGestion } from './corral';

export interface CorralesState {
  list: PaginatedList<Corral>;
  current: Corral;
  actives: Corral[];
  gestion: CorralParaGestion[];

  nuevo: () => void;
  cargarPaginados: (params: PaginationQueryParamsWithStatus) => Promise<void>;
  cargarActivos: () => Promise<void>;
  cargarPorId: (id: number) => Promise<void>;
  cargarPorCantidadGanado: (filtro: string) => Promise<void>;
  registrar: (request: RegistrarCorral) => Promise<number | null>;
  actualizar: (id: number, request: ActualizarCorral) => Promise<boolean>;
  activar: (id: number) => Promise<boolean>;
  desactivar: (id: number) => Promise<boolean>;
  traspasarIndividual: (request: TraspasarIndividualRequest) => Promise<boolean>;
  traspasarMasivo: (request: TraspasrMasivoRequest) => Promise<boolean>;
}

export interface RegistrarCorral {
  nombre: string;
}

export interface ActualizarCorral {
  nombre: string;
}

export interface TraspasarIndividualRequest {
  idCorralOrigen: number;
  idCorralDestino: number;
  fechaTraspaso: Date;
  folioInterno: string;
  partida: number;
}

export interface TraspasrMasivoRequest {
  idCorralOrigen: number;
  idCorralDestino: number;
  fechaTraspaso: Date;
  animales: Array<AnimalId>;
}

export interface AnimalId {
  partida: number;
  folioInterno: string;
}

const { showSuccessToast } = useUIStore.getState();

export const useCorralesStore = create<CorralesState>()((set, get) => ({
  list: {
    items: [],
    pageIndex: 0,
    pageSize: 10,
    totalRows: 0
  },
  current: {} as Corral,
  actives: [],
  gestion: [],

  nuevo: () =>
    set({
      current: {} as Corral
    }),
  cargarPaginados: async (params: PaginationQueryParamsWithStatus) => {
    const data = await makeGet<PaginatedList<Corral>>(
      `/corrales/paginados?${toQueryString(params)}`
    );

    if (data.isFailure) return;

    set({
      list: data.result
    });
  },
  cargarActivos: async () => {
    const data = await makeGet<Array<Corral>>('/corrales/activos');

    if (data.isFailure) return;

    set({
      actives: data.result
    });
  },
  cargarPorId: async (id: number) => {
    const data = await makeGet<Corral>(`/corrales/${id}`);

    if (data.isFailure) return;

    set({
      current: data.result
    });
  },
  cargarPorCantidadGanado: async (filtro: string) => {
    const data = await makeGet<Array<CorralParaGestion>>(`/corrales/por-cantidad-ganado/${filtro}`);

    if (data.isFailure) return;

    set({
      gestion: data.result
    });
  },
  registrar: async (request: RegistrarCorral) => {
    const data = await makePost<RegistrarCorral, number>('/corrales/', request);

    if (data.isFailure) return null;

    showSuccessToast('Registro agregado');

    set({
      current: {} as Corral
    });

    return data.result;
  },
  actualizar: async (id: number, request: ActualizarCorral) => {
    const data = await makePatch<ActualizarCorral, Corral>(`/corrales/${id}`, request);

    if (data.isFailure) return false;

    showSuccessToast('Registro actualizado');

    set({
      current: {
        ...get().current,
        ...request
      }
    });

    return true;
  },
  activar: async (id: number) => {
    const data = await makePatch(`/corrales/${id}/activar`);

    if (data.isFailure) return false;

    showSuccessToast('Registro activado');

    set({
      current: {
        ...get().current,
        activo: true
      }
    });

    return true;
  },
  desactivar: async (id: number) => {
    const data = await makePatch(`/corrales/${id}/desactivar`);

    if (data.isFailure) return false;

    showSuccessToast('Registro desactivado');

    set({
      current: {
        ...get().current,
        activo: false
      }
    });

    return true;
  },
  traspasarIndividual: async (request: TraspasarIndividualRequest) => {
    const data = await makePatch<TraspasarIndividualRequest, void>(
      '/corrales/traspaso-individual',
      request
    );

    if (data.isFailure) return false;

    showSuccessToast('Traspaso registrado');

    return true;
  },
  traspasarMasivo: async (request: TraspasrMasivoRequest) => {
    const data = await makePatch<TraspasrMasivoRequest, void>('/corrales/traspaso-masivo', request);

    if (data.isFailure) return false;

    showSuccessToast('Traspaso registrado');

    return true;
  }
}));
